import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'
import { placeholderWidth, SKELETON_TEST_ID } from '@/lib/skeleton'
import { cn } from '@/lib/utils'

export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className="flex flex-col space-y-3">
      <Skeleton className={cn('h-31.25 w-62.5 rounded-xl', className)} />
      <div className="space-y-2">
        <Skeleton className={cn('h-4 w-62.5', className)} />
        <Skeleton className={cn('h-4 w-50', className)} />
      </div>
    </div>
  )
}

export function SkeletonHeader({ className }: { className?: string }) {
  return (
    <div className="flex items-center justify-between">
      <Skeleton className={cn('h-8 w-32', className)} />
      <Skeleton className={cn('h-9 w-36', className)} />
    </div>
  )
}

/** Standalone table placeholder for routes that render before a `<Table>` exists. */
export function SkeletonTable({
  rows,
  columns,
  className,
}: {
  rows: number
  columns: number
  className?: string
}) {
  return (
    <div className="space-y-4">
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex gap-4">
          {Array.from({ length: columns }).map((_, column) => (
            <div key={column} className="min-w-0 flex-1">
              <Skeleton className={cn('h-4', placeholderWidth(row, column), className)} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Placeholder body rows for a live `<Table>` — drop into `<TableBody>` while the
 * first page is in flight so the loading state keeps the table's real layout
 * instead of collapsing to a single centred line of text.
 */
export function SkeletonTableRows({
  rows,
  columns,
  cellClassName,
}: {
  rows: number
  columns: number
  cellClassName?: string
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, row) => (
        <TableRow key={row} data-testid={SKELETON_TEST_ID} className="hover:bg-transparent">
          {Array.from({ length: columns }).map((_, column) => (
            <TableCell key={column} className={cellClassName}>
              <Skeleton className={cn('h-4', placeholderWidth(row, column))} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

/**
 * Page-level placeholder: a heading plus card panels of tabular content. The
 * default shape for dashboard-style routes that would otherwise flash a bare
 * "Loading…" line before their first render.
 */
export function SkeletonPanels({
  panels = 2,
  rows = 5,
  columns = 4,
}: {
  panels?: number
  rows?: number
  columns?: number
}) {
  return (
    <div className="space-y-6 p-6" data-testid={SKELETON_TEST_ID}>
      <div className="space-y-2">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      {Array.from({ length: panels }).map((_, panel) => (
        <div key={panel} className="space-y-4 rounded-lg border p-6">
          <Skeleton className="h-6 w-40" />
          <SkeletonTable rows={rows} columns={columns} />
        </div>
      ))}
    </div>
  )
}

/**
 * Placeholder for a vertical list of records (inbox items, history events,
 * mobile cards) — a leading avatar/icon block plus two text lines per item.
 */
export function SkeletonList({
  items,
  className,
  itemClassName,
  showLeading = true,
}: {
  items: number
  className?: string
  itemClassName?: string
  showLeading?: boolean
}) {
  return (
    <div className={cn('space-y-3', className)} data-testid={SKELETON_TEST_ID}>
      {Array.from({ length: items }).map((_, item) => (
        <div
          key={item}
          className={cn('flex items-start gap-3 rounded-md border bg-background p-3', itemClassName)}
        >
          {showLeading && <Skeleton className="size-8 shrink-0 rounded-full" />}
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className={cn('h-4', placeholderWidth(item, 0))} />
            <Skeleton className={cn('h-3', placeholderWidth(item, 2))} />
          </div>
        </div>
      ))}
    </div>
  )
}
